import React, { useCallback, useEffect, useRef } from "react";
import { propTypes } from "redux-form";
import get from "lodash/get";
import uniqWith from "lodash/uniqWith";
import isEqual from "lodash/isEqual";
import { fields as paymentFields } from "app/pages/Account/MyBookings/MyBookingsPayment/MyBookingsPaymentSchema.js";
import { FormattedMessage } from "react-intl";
import { anyFieldTouched } from "app/utils/form-validator";
import Button from "app/pages/.shared/form/Button";
import AlertMessage from "app/pages/.shared/AlertMessage";
import SecurityNote from "app/pages/Booking/Payment/SecurityNote";
import CreditCardFieldset from "app/pages/Booking/Payment/CreditCardFieldset/CreditCardFieldset";
import { ALERT_TYPE, CREDIT_CARDS_TYPES } from "app/constants";
import { dependantPaymentCardFields } from "./MyBookingsPaymentSchema";

import "./MyBookingsPayment.scss";
import { scrollToElement } from "app/utils/scroller";

function triggerChangeOnOtherDependantPaymentCardFields(touchedFieldName, fields) {
	dependantPaymentCardFields.forEach(fieldName => {
		if (fieldName !== touchedFieldName && fields.payment.card[fieldName].value) {
			fields.payment.card[fieldName].onChange(fields.payment.card[fieldName].value);
		}
	});
}

const MyBookingsPayment = ({
	fields,
	invalid,
	error,
	errors,
	submitting,
	handleSubmit,
	submitFailed,
	shop,
	brandDisplayName,
}) => {
	const errorRef = useRef();

	const handleCreditCardInformationChange = useCallback(
		event => {
			const { fieldName: touchedFieldName } = event.target.dataset;
			fields.payment.card[touchedFieldName].onChange(event);
			triggerChangeOnOtherDependantPaymentCardFields(touchedFieldName, fields);
		},
		[fields]
	);

	const allPaymentErrorsObjects = paymentFields.reduce((acc, path) => {
		const error = get(errors, path);
		return error ? [...acc, error] : acc;
	}, []);
	const uniqPaymentErrorsObjects = uniqWith(allPaymentErrorsObjects, isEqual);
	const fieldsErrors = uniqPaymentErrorsObjects.map(error => (
		<FormattedMessage key={error.id} {...error} />
	));

	const showErrorMessage =
		((anyFieldTouched(fields) && invalid) || submitFailed) && (error || errors.length > 0);

	useEffect(() => {
		if (showErrorMessage) {
			scrollToElement(errorRef.current, -100, true);
		}
	}, [showErrorMessage]);

	let acceptedCreditCards = [
		CREDIT_CARDS_TYPES.VISA,
		CREDIT_CARDS_TYPES.VISA_ELECTRON,
		CREDIT_CARDS_TYPES.MASTERCARD,
		CREDIT_CARDS_TYPES.AMEX,
	];

	if (shop === "fr-FR") {
		acceptedCreditCards = [CREDIT_CARDS_TYPES.CB].concat(acceptedCreditCards);
	}

	return (
		<form className="my-booking-payment" onSubmit={handleSubmit}>
			<div className="my-booking-item__title">
				<FormattedMessage id="my.bookings.show.payment.form.button.label" />
			</div>

			<div className="my-booking-payment__note">
				<FormattedMessage id="creditcard.amount.help.label" />
			</div>

			<div className="my-booking-payment__security-note">
				<SecurityNote
					acceptedCreditCards={acceptedCreditCards}
					brandDisplayName={brandDisplayName}
				/>
			</div>

			{showErrorMessage && (
				<div className="my-booking-payment__error" ref={errorRef}>
					<AlertMessage
						message={<FormattedMessage {...error} />}
						errors={fieldsErrors}
						alertType={ALERT_TYPE.ERROR}
					/>
				</div>
			)}

			<CreditCardFieldset
				fields={fields.payment.card}
				showAmountToPay={true}
				showAmountToPayNote={false}
				isAmountToPayRequired={true}
				isAmexAvailable={true}
				onCreditCardInformationChange={handleCreditCardInformationChange}
			/>

			<div className="my-booking-payment__action">
				<Button
					submit
					loading={submitting}
					design="primary"
					data-cy="my-booking-payment-button"
				>
					<FormattedMessage id="my.bookings.pay.button.label" />
				</Button>
			</div>
		</form>
	);
};

MyBookingsPayment.propTypes = {
	...propTypes,
};

export default MyBookingsPayment;
